import { template as _$template } from "solid-js/web";
import { use as _$use } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<label title="Both national ensigns ship in the one .glb and the sim shows one — docs/engineering/ship-design.md §5.">Ensign<select><option value=entente>Entente (White Ensign)</option><option value=central>Central Powers (Reichskriegsflagge)`),
  _tmpl$2 = /*#__PURE__*/_$template(`<span class=model-viewer__message>No enhanced props — <code>data/enhanced</code> is optional and is written by <code>bun run enhanced</code>.`),
  _tmpl$3 = /*#__PURE__*/_$template(`<p class=model-viewer__message>WebGPU is not available in this browser/context (<code>navigator.gpu</code> is undefined). This port targets three.js <code>WebGPURenderer</code> only — no WebGL fallback (AD-005).`),
  _tmpl$4 = /*#__PURE__*/_$template(`<p class=model-viewer__message>Initializing WebGPU…`),
  _tmpl$5 = /*#__PURE__*/_$template(`<p class="model-viewer__message model-viewer__message--error">WebGPU failed to initialize: `),
  _tmpl$6 = /*#__PURE__*/_$template(`<div class=model-viewer__overlay>`),
  _tmpl$7 = /*#__PURE__*/_$template(`<div class=model-viewer><div class=model-viewer__toolbar><label title="Models in data/enhanced that no baked asset explains — built from a citable object of the period rather than from a .TBL.">Prop<select></select></label></div><div class=model-viewer__canvas-wrap><div class=model-viewer__canvas-host>`),
  _tmpl$8 = /*#__PURE__*/_$template(`<option>`);
import { createEffect, createResource, createSignal, For, onCleanup, Show } from "solid-js";
import { loadEnhancedModel } from "../../three/enhanced-model.ts";
import { enhancedIndex } from "../../three/enhanced-catalog.ts";
import { setEnsignSide } from "../../three/enhanced-nodes.ts";
import { createModelScene } from "../three-model-scene.ts";
import { hasWebGpu } from "../../three/webgpu-support.ts";

/**
 * The turntable for an **enhanced prop** — a model in `data/enhanced` that no
 * baked asset explains.
 *
 * `ModelViewer` is driven by a bake-manifest entry: it fetches `models/<id>.json`
 * and offers the enhanced `.glb` beside it as a comparison. A prop has no such
 * entry and never will, because the original ships no asset for it — the
 * searchlight (AD-017 §10) and the escort sloop
 * (`docs/engineering/ship-design.md`) are both built from a citable object of
 * the period and a palette. Before this pane they were the only things in
 * `data/enhanced` that could not be looked at without writing a script.
 *
 * Everything else about the pane is `ModelViewer`'s, deliberately: the same
 * `createModelScene`, the same WebGPU feature detection with no WebGL fallback
 * (AD-005), the same orbit-controlled camera framed on the subject's own bounds.
 * The only difference is that there is nothing to compare against, so the scene
 * is put straight into `enhanced` mode and never leaves it.
 */

/**
 * Which enhanced entries are props: the ones whose id names no baked model.
 *
 * Derived rather than listed. A third prop generator added to
 * `tools/enhanced/src/index.ts` shows up here without anyone remembering to add
 * it, and a prop that gains a baked counterpart (which would mean the original
 * turned out to have the asset after all) drops out of here and into
 * `ModelViewer`'s comparison, where it belongs.
 */
export function propEntries(baked) {
  const index = enhancedIndex();
  if (!index) return [];
  return [...index.entries()].filter(([id]) => !baked.has(id)).map(([id, entry]) => ({
    id,
    path: entry.path
  })).sort((a, b) => a.id.localeCompare(b.id));
}

/** `models/ship` -> `ship`, which is what the list shows and what the generator calls it. */
export function propLabel(id) {
  return id.replace(/^models\//, "");
}
export function PropViewer(props) {
  const entries = () => propEntries(props.baked);
  const [selected, setSelected] = createSignal(undefined);
  const current = () => {
    const wanted = selected();
    const list = entries();
    return list.find(entry => entry.id === wanted) ?? list[0];
  };
  const [status, setStatus] = createSignal(hasWebGpu() ? "initializing" : "unavailable");
  const [errorMessage, setErrorMessage] = createSignal(undefined);
  // The scene owns and disposes whatever model it is handed, so a reload has to
  // be keyed on the scene as well as the path — `ModelViewer`'s `sceneEpoch`,
  // for the same reason: two scenes must never share one loaded object.
  const [sceneEpoch, setSceneEpoch] = createSignal(0);
  const [model] = createResource(() => {
    const entry = current();
    return entry === undefined ? undefined : {
      path: entry.path,
      epoch: sceneEpoch()
    };
  }, source => loadEnhancedModel(source.path));
  let containerRef;
  let scene;
  function teardownScene() {
    scene?.dispose();
    scene = undefined;
  }
  onCleanup(teardownScene);
  createEffect(() => {
    void current()?.id; // track: a new prop selection re-initializes the scene
    teardownScene();
    if (!hasWebGpu() || !containerRef) {
      setStatus(hasWebGpu() ? "initializing" : "unavailable");
      return;
    }
    setStatus("initializing");
    setErrorMessage(undefined);
    setSceneEpoch(epoch => epoch + 1);
    scene = createModelScene(containerRef, {
      onStatus: (nextStatus, message) => {
        setStatus(nextStatus);
        setErrorMessage(message);
      }
    });
    // There is no authentic model to compare against and there never will be,
    // so the scene shows the `.glb` alone. `layout` frames whatever is visible.
    scene.setComparison("enhanced");
  });

  // Which national ensign a prop that carries both is flying. Zero for every
  // prop that carries none, which hides the control — this is the escort sloop's
  // one departure from "a prop is just a mesh", and the pane asks the model
  // rather than knowing which prop it is.
  const [side, setSide] = createSignal("entente");
  const [ensigns, setEnsigns] = createSignal(0);
  createEffect(() => {
    const loaded = model();
    if (status() !== "ready" || !scene) return;
    setEnsigns(loaded ? setEnsignSide(loaded, side()) : 0);
    scene.setEnhanced(loaded);
    scene.setComparison("enhanced");
  });

  // A later flag change re-runs on the model already on show, without reloading
  // it — the whole argument for two ensigns in one file.
  createEffect(() => {
    const loaded = model();
    if (loaded) setEnsigns(setEnsignSide(loaded, side()));
  });
  return (() => {
    var _el$ = _tmpl$7(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.firstChild,
      _el$4 = _el$3.firstChild,
      _el$5 = _el$4.nextSibling,
      _el$0 = _el$2.nextSibling,
      _el$1 = _el$0.firstChild;
    _el$5.addEventListener("change", event => setSelected(event.currentTarget.value));
    _$insert(_el$5, _$createComponent(For, {
      get each() {
        return entries();
      },
      children: entry => (() => {
        var _el$15 = _tmpl$8();
        _$insert(_el$15, () => propLabel(entry.id));
        _$effect(() => _el$15.value = entry.id);
        return _el$15;
      })()
    }));
    _$insert(_el$2, _$createComponent(Show, {
      get when() {
        return ensigns() > 0;
      },
      get children() {
        var _el$6 = _tmpl$(),
          _el$7 = _el$6.firstChild,
          _el$8 = _el$7.nextSibling;
        _el$8.addEventListener("change", event => setSide(event.currentTarget.value));
        _$effect(() => _el$8.value = side());
        return _el$6;
      }
    }), null);
    _$insert(_el$2, _$createComponent(Show, {
      get when() {
        return entries().length === 0;
      },
      get children() {
        return _tmpl$2();
      }
    }), null);
    var _ref$ = containerRef;
    typeof _ref$ === "function" ? _$use(_ref$, _el$1) : containerRef = _el$1;
    _$insert(_el$0, _$createComponent(Show, {
      get when() {
        return status() !== "ready";
      },
      get children() {
        var _el$10 = _tmpl$6();
        _$insert(_el$10, _$createComponent(Show, {
          get when() {
            return status() === "unavailable";
          },
          get children() {
            return _tmpl$3();
          }
        }), null);
        _$insert(_el$10, _$createComponent(Show, {
          get when() {
            return status() === "initializing";
          },
          get children() {
            return _tmpl$4();
          }
        }), null);
        _$insert(_el$10, _$createComponent(Show, {
          get when() {
            return status() === "error";
          },
          get children() {
            var _el$13 = _tmpl$5(),
              _el$14 = _el$13.firstChild;
            _$insert(_el$13, errorMessage, null);
            return _el$13;
          }
        }), null);
        return _el$10;
      }
    }), null);
    _$effect(() => _el$5.disabled = entries().length === 0);
    _$effect(() => _el$5.value = current()?.id ?? "");
    return _el$;
  })();
}